import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Link, Route, Switch, Redirect, useHistory, useLocation } from "react-router-dom";
import {
  CompanyForm,
  ContactForm,
  Onboarding,
  OTPVerification,
} from "../Registration/index";
import Stepper from "../components/Stepper";
import messages from "../../../assets/locale/messages";
import { AUTH_CONTEXT_TYPE } from "../../../utils/authPortal";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  getCompanyRegistrationStepRoute,
  getResolvedCompanyPortalSession,
  isAllowedCompanySignupStepPath,
  isCompanyRegistrationIncompleteState,
  isCompanySignupPath,
  normalizeCompanySignupPathname,
  persistCompanyPortalSession,
} from "../../../utils/companyRegistration";
import "./RegistrationFlow.scss";

/** Step order for the 3-segment stepper: contact → OTP → company */
const FORM_PROGRESS_STEP_PATHS = [
  ROUTE_PATHS.companySignupContact,
  ROUTE_PATHS.companySignupOtp,
  ROUTE_PATHS.companySignupCompany,
];

const FORM_PROGRESS_TOTAL = FORM_PROGRESS_STEP_PATHS.length;

const formatStepProgressLabel = (template, stepDisplay, totalDisplay) =>
  template.replace("{step}", stepDisplay).replace("{total}", totalDisplay);

/**
 * RegistrationFlow — nested routes under /company/signup.
 * Stepper UI unchanged; order is contact → OTP → company (pending is `/company/pending-approval`).
 */
const RegistrationFlow = ({ match, type = AUTH_CONTEXT_TYPE.COMPANY }) => {
  const history = useHistory();
  const location = useLocation();
  const signupPath = useMemo(
    () => normalizeCompanySignupPathname(location.pathname),
    [location.pathname],
  );
  const login = useSelector((state) => state.auth.login);
  const lang = useSelector((state) => state.locale.lang);
  const { registrationFlow: rf } = messages[lang];

  useEffect(() => {
    if (!isCompanySignupPath(location.pathname)) return;
    if (location.pathname !== signupPath) {
      history.replace(`${signupPath}${location.search}${location.hash}`);
    }
  }, [location.pathname, signupPath, history, location.search, location.hash]);

  useEffect(() => {
    if (!isCompanySignupPath(signupPath)) return;
    const target = getCompanyRegistrationStepRoute();
    if (target === ROUTE_PATHS.companyHome) {
      const a = getResolvedCompanyPortalSession();
      if (a?.auth_token && !isCompanyRegistrationIncompleteState(a)) {
        history.replace(ROUTE_PATHS.companyHome);
      }
      return;
    }
    if (isAllowedCompanySignupStepPath(signupPath)) {
      return;
    }
    if (signupPath !== target) {
      history.replace(target);
    }
  }, [signupPath, login, history]);

  const formProgressIndex = useMemo(() => {
    const i = FORM_PROGRESS_STEP_PATHS.indexOf(signupPath);
    return i;
  }, [signupPath]);

  const showFormProgress = formProgressIndex >= 0;

  const stepProgressLabel = useMemo(() => {
    if (!showFormProgress) return "";
    const stepNum = formProgressIndex + 1;
    return formatStepProgressLabel(
      rf.stepProgress,
      String(stepNum).padStart(2, "0"),
      String(FORM_PROGRESS_TOTAL).padStart(2, "0")
    );
  }, [showFormProgress, formProgressIndex, rf.stepProgress]);

  const handleOtpVerified = (data) => {
    persistCompanyPortalSession(data);
    history.push(ROUTE_PATHS.companySignupCompany);
  };

  const handleCompanyComplete = (data) => {
    persistCompanyPortalSession(data);
    history.push(ROUTE_PATHS.companyPendingApproval);
  };

  return (
    <div
      className={`registration-flow${
        showFormProgress ? " registration-flow--with-stepper" : ""
      }`}
      data-auth-portal={type}
    >
      {showFormProgress && (
        <Stepper
          currentStep={formProgressIndex + 1}
          totalSteps={FORM_PROGRESS_TOTAL}
          label={stepProgressLabel}
        />
      )}

      <div>
        <Switch>
          <Route path={match.path} exact>
            <Onboarding
              onNext={() => history.push(ROUTE_PATHS.companySignupContact)}
            />
          </Route>
          <Route path={ROUTE_PATHS.companySignupContact} exact>
            <ContactForm />
            <p className="auth-inline-prompt auth-inline-prompt--md auth-inline-prompt--center registration-flow__login-footer mb-0">
              <span className="auth-inline-prompt__text">
                {rf.alreadyHaveAccount}
              </span>
              <Link
                to={ROUTE_PATHS.companyLogin}
                className="auth-inline-prompt__link"
              >
                {rf.logIn}
              </Link>
            </p>
          </Route>
          <Route path={ROUTE_PATHS.companySignupOtp} exact>
            <OTPVerification onSubmit={handleOtpVerified} />
          </Route>
          <Route path={ROUTE_PATHS.companySignupCompany} exact>
            <CompanyForm onComplete={handleCompanyComplete} />
          </Route>
          <Redirect to={ROUTE_PATHS.companySignup} />
        </Switch>
      </div>
    </div>
  );
};

RegistrationFlow.propTypes = {
  match: PropTypes.shape({
    path: PropTypes.string,
  }).isRequired,
  type: PropTypes.oneOf(Object.values(AUTH_CONTEXT_TYPE)),
};

export default RegistrationFlow;
